import React, { FC } from "react";
import type { SupportCard } from "../../services/ApiService";
import {
  Card,
  CardChip,
  CardChips,
  CardFact,
  CardFacts,
  CardState,
  CardSubtitle,
  CardTitle,
} from "./SupportAssistant.styles";

/**
 * One production order, drawn where the answer put it.
 *
 * ⚠️ EVERY FIELD HERE IS RENDERED AS TEXT, NOT AS MARKUP — AND THAT IS THE POINT OF THE
 * COMPONENT.
 * ------------------------------------------------------------------------------------
 * `AnswerText` runs `renderInline` over the prose so the model's `**bold**` becomes emphasis.
 * Nothing on this card goes through it. A customer called `**Acme**` must appear as
 * `**Acme**`, not as a bold Acme: these values came out of the ERP and out of job records, not
 * out of the model, so interpreting markup in them would mean data deciding how it is
 * displayed. That is the one place in this panel where model-adjacent markup would be applied
 * to something nobody wrote as markup.
 *
 * ⚠️ AND THE MODEL WROTE NONE OF IT. It chose the card's position and the order number; the
 * Function App filled the fields in from a lookup it had already made (`CardGuard.cs`,
 * `CardLedger.cs`). The prompt's own rule is "never invent a number, quantity, date, status or
 * name", and a card is exactly where an invented one would look most official — so the model
 * is given no way to author one.
 *
 * ⚠️ `late` IS TRI-STATE. Absent means NOT KNOWABLE — no scheduled end date, or nothing open
 * left to be late for — and the chip is then simply absent. It is never rendered as "on time",
 * which would be a claim nobody made, and a finished order gets no Late chip however overdue
 * its date was: it *was* late, which is a different sentence and one this card cannot make.
 */
interface Props {
  card: SupportCard;
}

/** A number the server sent, or nothing. `0` is a real answer and must survive. */
const isNumber = (value?: number): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * "2 of 5" — but only when there is a total. An order CSM knows but has no routings for has
 * none, and "0 of 0" reads as a fault rather than as an absence.
 */
const progress = (complete?: number, total?: number): string | null =>
  isNumber(total) && total > 0 ? `${isNumber(complete) ? complete : 0} of ${total}` : null;

/** "40 of 100", or just the one that is known. */
const quantity = (completed?: number, ordered?: number): string | null => {
  if (isNumber(ordered)) return `${isNumber(completed) ? completed : 0} of ${ordered}`;
  return isNumber(completed) ? String(completed) : null;
};

const OrderCard: FC<Props> = ({ card }) => {
  const facts: Array<{ label: string; value: string }> = [];

  const made = quantity(card.quantityCompleted, card.quantityOrdered);
  if (made) facts.push({ label: "Quantity", value: made });

  const steps = progress(card.operationsComplete, card.operationsTotal);
  if (steps) facts.push({ label: "Operations", value: steps });

  if (card.scheduledEndDate) facts.push({ label: "Due", value: card.scheduledEndDate });
  if (card.customer) facts.push({ label: "Customer", value: card.customer });
  if (card.warehouse) facts.push({ label: "Warehouse", value: card.warehouse });

  return (
    <Card aria-label={`Production order ${card.title}`}>
      <CardTitle>{card.title}</CardTitle>
      {card.subtitle && <CardSubtitle>{card.subtitle}</CardSubtitle>}

      {card.state && <CardState>{card.state}</CardState>}

      {(card.late === true || card.onShopFloorBoard || card.beingWorkedOnNow) && (
        <CardChips>
          {/* Explicitly `=== true`: undefined and null both mean "not knowable" here, and a
              falsy check would collapse them into "not late", which is a different claim. */}
          {card.late === true && <CardChip $tone="late">Late</CardChip>}
          {card.onShopFloorBoard && <CardChip>On the board</CardChip>}
          {card.beingWorkedOnNow && <CardChip>Being worked on</CardChip>}
        </CardChips>
      )}

      {facts.length > 0 && (
        <CardFacts>
          {facts.map((fact) => (
            <CardFact key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </CardFact>
          ))}
        </CardFacts>
      )}
    </Card>
  );
};

export default OrderCard;
